import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authedFetch } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

interface Props {
  tenantCode: string;
  chatId: string;
}

interface WpCustomer {
  configured: boolean;
  found?: boolean;
  id?: number;
  email?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  billing_phone?: string;
  billing_city?: string;
  billing_country?: string;
  orders_count?: number;
  total_spent?: number;
  registered?: string;
  reason?: string;
}

interface WpOrder {
  id: number;
  number: string;
  status: string;
  total: number;
  currency: string;
  created_at: number | null;
  paid_at: number | null;
  items: Array<{ name: string; quantity: number; total: number }>;
  edit_url: string;
}

interface WpOrders {
  configured: boolean;
  items?: WpOrder[];
  count?: number;
  reason?: string;
}

export function WpCustomerPanel({ tenantCode, chatId }: Props) {
  const { user } = useAuth();
  const [customer, setCustomer] = useState<WpCustomer | null>(null);
  const [orders, setOrders] = useState<WpOrders | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const qs = `?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`;
        const [c, o] = await Promise.all([
          authedFetch<WpCustomer>(`/agent/wp/customer${qs}`),
          authedFetch<WpOrders>(`/agent/wp/orders${qs}&limit=5`),
        ]);
        if (cancelled) return;
        setCustomer(c);
        setOrders(o);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantCode, chatId, user]);

  if (loading) {
    return (
      <div className="px-4 py-3 text-xs text-gray-500">Loading WooCommerce…</div>
    );
  }
  if (error) {
    return <ErrorBanner error={error} label="WP lookup error" className="px-4 py-3 text-xs text-red-600" />;
  }
  if (customer && customer.configured === false) {
    return (
      <div className="px-4 py-3 text-xs text-gray-400">
        WooCommerce bridge not configured for this tenant.
      </div>
    );
  }
  if (customer && customer.configured && customer.found === false) {
    return (
      <div className="px-4 py-3 text-xs text-gray-500">
        No WooCommerce customer match {customer.reason ? `(${customer.reason})` : ''}.
      </div>
    );
  }

  return (
    <div className="px-4 py-3 text-xs text-gray-700">
      <div className="font-semibold uppercase tracking-wide text-gray-500 mb-1.5">WooCommerce</div>
      {customer && customer.found && (
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-gray-900">
            {customer.display_name || `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() || customer.email}
          </div>
          {customer.email && <div className="text-gray-500">{customer.email}</div>}
          {customer.billing_phone && <div className="text-gray-500">{customer.billing_phone}</div>}
          {(customer.billing_city || customer.billing_country) && (
            <div className="text-gray-500">{[customer.billing_city, customer.billing_country].filter(Boolean).join(', ')}</div>
          )}
          {(customer.orders_count !== undefined || customer.total_spent !== undefined) && (
            <div className="mt-1 flex gap-3 text-[11px]">
              {customer.orders_count !== undefined && (
                <span><span className="text-gray-400">orders</span> <span className="font-medium">{customer.orders_count}</span></span>
              )}
              {customer.total_spent !== undefined && (
                <span><span className="text-gray-400">spent</span> <span className="font-medium">{customer.total_spent.toFixed(2)}</span></span>
              )}
            </div>
          )}
        </div>
      )}
      {orders && orders.items && orders.items.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Recent orders</div>
          <ul className="space-y-1">
            {orders.items.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-2">
                <a href={o.edit_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                  #{o.number}
                </a>
                <span className="text-gray-500 truncate flex-1">
                  {o.items.map((i) => i.name).join(', ')}
                </span>
                <span className="font-medium whitespace-nowrap">
                  {o.total.toFixed(2)} {o.currency}
                </span>
                <span className="text-[10px] uppercase text-gray-400">{o.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {orders && orders.items && orders.items.length === 0 && (
        <div className="mt-2 text-[11px] text-gray-400">No orders.</div>
      )}
    </div>
  );
}
